import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAgents } from "@/services/templateService";
import {
  voiceAgentService,
  VoiceAgent,
  VoiceAgentStats,
} from "@/services/voiceAgentService";
import { featureFlagService } from "@/services/featureFlags";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

// Types
export interface Agent extends Tables<"agents"> {
  // Extend with any computed properties if needed
}

export interface AgentStats {
  total: number;
  last30Days: number;
  last24Hours: number;
}

export interface AgentState {
  // Chat Agents (deprecated - kept for compatibility)
  chatAgents: Agent[];
  activeChatAgents: Agent[];
  isLoadingChatAgents: boolean;
  chatAgentsError: Error | null;
  chatAgentsLastFetch: number | null;
  chatAgentStats: Record<string, AgentStats>;

  // Voice Agents (primary type)
  voiceAgents: VoiceAgent[];
  activeVoiceAgents: VoiceAgent[];
  isLoadingVoiceAgents: boolean;
  voiceAgentsError: Error | null;
  voiceAgentsLastFetch: number | null;
  voiceAgentStats: Record<string, VoiceAgentStats>;
  voiceAgentsEnabled: boolean;
  isLoadingVoiceFeature: boolean;
  isLoadingVoiceStats: boolean;
  voiceStatsLoadingAgents: Set<string>;

  // Global state
  isInitialized: boolean;
  organizationId: string | null;
}

export interface AgentContextType extends AgentState {
  // Actions
  refreshAgents: () => Promise<void>;
  refreshChatAgents: () => Promise<void>; // Deprecated - no-op
  refreshVoiceAgents: () => Promise<void>;
  invalidateAgent: (agentId: string, type: "chat" | "voice") => void;
  invalidateAllAgents: () => void;
  reset: () => void;

  // Computed properties
  allAgents: (Agent | VoiceAgent)[];
  allActiveAgents: (Agent | VoiceAgent)[];
  totalAgentCount: number;
  isLoading: boolean;
  hasError: boolean;
}

// Action types
type AgentAction =
  | { type: "RESET" }
  | { type: "SET_ORGANIZATION"; payload: string | null }
  | {
      type: "SET_VOICE_FEATURE";
      payload: { enabled: boolean; loading: boolean };
    }
  | { type: "CHAT_AGENTS_LOADING"; payload: boolean }
  | {
      type: "CHAT_AGENTS_SUCCESS";
      payload: { agents: Agent[]; stats: Record<string, AgentStats> };
    }
  | { type: "CHAT_AGENTS_ERROR"; payload: Error }
  | { type: "VOICE_AGENTS_LOADING"; payload: boolean }
  | {
      type: "VOICE_AGENTS_SUCCESS";
      payload: {
        agents: VoiceAgent[];
        stats?: Record<string, VoiceAgentStats>;
      };
    }
  | { type: "VOICE_AGENTS_ERROR"; payload: Error }
  | {
      type: "VOICE_STATS_LOADING";
      payload: { agentIds?: string[]; loading: boolean };
    }
  | {
      type: "VOICE_STATS_SUCCESS";
      payload: { stats: Record<string, VoiceAgentStats> };
    }
  | { type: "VOICE_STATS_ERROR"; payload: Error }
  | {
      type: "INVALIDATE_AGENT";
      payload: { agentId: string; type: "chat" | "voice" };
    }
  | { type: "INVALIDATE_ALL" }
  | { type: "SET_INITIALIZED"; payload: boolean };

// Default state
const createDefaultState = (
  organizationId: string | null = null,
): AgentState => ({
  // Chat agents - deprecated but kept for compatibility
  chatAgents: [],
  activeChatAgents: [],
  isLoadingChatAgents: false,
  chatAgentsError: null,
  chatAgentsLastFetch: null,
  chatAgentStats: {},

  // Voice agents - primary type
  voiceAgents: [],
  activeVoiceAgents: [],
  isLoadingVoiceAgents: false,
  voiceAgentsError: null,
  voiceAgentsLastFetch: null,
  voiceAgentStats: {},
  voiceAgentsEnabled: false,
  isLoadingVoiceFeature: false,
  isLoadingVoiceStats: false,
  voiceStatsLoadingAgents: new Set(),

  isInitialized: false,
  organizationId,
});

// Reducer
function agentReducer(state: AgentState, action: AgentAction): AgentState {
  switch (action.type) {
    case "RESET":
      return createDefaultState(state.organizationId);

    case "SET_ORGANIZATION":
      if (state.organizationId === action.payload) return state;
      return createDefaultState(action.payload);

    case "SET_VOICE_FEATURE":
      return {
        ...state,
        voiceAgentsEnabled: action.payload.enabled,
        isLoadingVoiceFeature: action.payload.loading,
      };

    case "CHAT_AGENTS_LOADING":
      return {
        ...state,
        isLoadingChatAgents: action.payload,
        chatAgentsError: action.payload ? null : state.chatAgentsError,
      };

    case "CHAT_AGENTS_SUCCESS": {
      const activeAgents = action.payload.agents.filter(
        (agent) => agent.is_active === true,
      );
      return {
        ...state,
        chatAgents: action.payload.agents,
        activeChatAgents: activeAgents,
        chatAgentStats: action.payload.stats,
        isLoadingChatAgents: false,
        chatAgentsError: null,
        chatAgentsLastFetch: Date.now(),
      };
    }

    case "CHAT_AGENTS_ERROR":
      return {
        ...state,
        isLoadingChatAgents: false,
        chatAgentsError: action.payload,
      };

    case "VOICE_AGENTS_LOADING":
      return {
        ...state,
        isLoadingVoiceAgents: action.payload,
        voiceAgentsError: action.payload ? null : state.voiceAgentsError,
      };

    case "VOICE_AGENTS_SUCCESS": {
      const activeAgents = action.payload.agents.filter(
        (agent) => agent.is_active === true,
      );
      return {
        ...state,
        voiceAgents: action.payload.agents,
        activeVoiceAgents: activeAgents,
        voiceAgentStats: action.payload.stats || state.voiceAgentStats,
        isLoadingVoiceAgents: false,
        voiceAgentsError: null,
        voiceAgentsLastFetch: Date.now(),
      };
    }

    case "VOICE_AGENTS_ERROR":
      return {
        ...state,
        isLoadingVoiceAgents: false,
        voiceAgentsError: action.payload,
      };

    case "VOICE_STATS_LOADING": {
      const newLoadingAgents = new Set(state.voiceStatsLoadingAgents);
      if (action.payload.loading && action.payload.agentIds) {
        action.payload.agentIds.forEach((id) => newLoadingAgents.add(id));
      } else if (!action.payload.loading) {
        newLoadingAgents.clear();
      }
      return {
        ...state,
        isLoadingVoiceStats: action.payload.loading,
        voiceStatsLoadingAgents: newLoadingAgents,
      };
    }

    case "VOICE_STATS_SUCCESS":
      return {
        ...state,
        voiceAgentStats: { ...state.voiceAgentStats, ...action.payload.stats },
        isLoadingVoiceStats: false,
        voiceStatsLoadingAgents: new Set(),
      };

    case "VOICE_STATS_ERROR":
      return {
        ...state,
        isLoadingVoiceStats: false,
        voiceStatsLoadingAgents: new Set(),
      };

    case "INVALIDATE_AGENT": {
      if (action.payload.type === "chat") {
        return {
          ...state,
          chatAgentsLastFetch: null,
        };
      } else {
        return {
          ...state,
          voiceAgentsLastFetch: null,
        };
      }
    }

    case "INVALIDATE_ALL":
      return {
        ...state,
        chatAgentsLastFetch: null,
        voiceAgentsLastFetch: null,
      };

    case "SET_INITIALIZED":
      return {
        ...state,
        isInitialized: action.payload,
      };

    default:
      return state;
  }
}

// Default context value with reasonable defaults and error boundaries
const createDefaultContextValue = (): AgentContextType => ({
  ...createDefaultState(),
  refreshAgents: async () => {
    throw new Error(
      "AgentContext: refreshAgents called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  refreshChatAgents: async () => {
    throw new Error(
      "AgentContext: refreshChatAgents called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  refreshVoiceAgents: async () => {
    throw new Error(
      "AgentContext: refreshVoiceAgents called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  invalidateAgent: () => {
    throw new Error(
      "AgentContext: invalidateAgent called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  invalidateAllAgents: () => {
    throw new Error(
      "AgentContext: invalidateAllAgents called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  reset: () => {
    throw new Error(
      "AgentContext: reset called outside of AgentProvider. " +
        "Ensure your component is wrapped with <AgentProvider>.",
    );
  },
  allAgents: [],
  allActiveAgents: [],
  totalAgentCount: 0,
  isLoading: false,
  hasError: false,
});

// Context
const AgentContext = createContext<AgentContextType>(
  createDefaultContextValue(),
);

// Provider props
export interface AgentProviderProps {
  children: ReactNode;

  // Override options for testing/storybook
  defaultState?: Partial<AgentState>;
  cacheTimeout?: number; // in milliseconds
  disabled?: boolean; // Disable all fetching for testing

  // Composition support
  fallback?: ReactNode; // Loading fallback
  errorBoundary?: boolean; // Whether to include error boundary
}

// Cache timeout constant
const DEFAULT_CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export function AgentProvider({
  children,
  defaultState,
  cacheTimeout = DEFAULT_CACHE_TIMEOUT,
  disabled = false,
  fallback,
  errorBoundary = true,
}: AgentProviderProps) {
  const { user, organization, isLoading: isAuthLoading } = useAuth();

  // Initialize state with defaults and overrides
  const initialState = useMemo(
    () => ({
      ...createDefaultState(organization?.id || null),
      ...defaultState,
    }),
    [organization?.id, defaultState],
  );

  const [state, dispatch] = useReducer(agentReducer, initialState);

  // Refs for side-effect containment and cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount - side-effect containment
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Helper to check if data is stale
  const isDataStale = useCallback(
    (lastFetch: number | null) => {
      if (!lastFetch) return true;
      return Date.now() - lastFetch > cacheTimeout;
    },
    [cacheTimeout],
  );

  // Fetch chat agent stats
  const fetchChatAgentStats = useCallback(
    async (agents: Agent[]) => {
      if (!organization?.id || agents.length === 0) return {};

      const statsMap: Record<string, AgentStats> = {};
      const activeAgents = agents.filter((agent) => agent.is_active === true);

      // Import supabase dynamically to avoid SSR issues
      const { supabase } = await import("@/integrations/supabase/client");

      for (const agent of activeAgents) {
        try {
          const now = new Date();
          const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

          // Fetch total conversations
          const { count: totalCount } = await supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id);

          // Fetch last 30 days conversations
          const { count: last30DaysCount } = await supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .gte("created_at", last30Days.toISOString());

          // Fetch last 24 hours conversations
          const { count: last24HoursCount } = await supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .gte("created_at", last24Hours.toISOString());

          statsMap[agent.id] = {
            total: totalCount || 0,
            last30Days: last30DaysCount || 0,
            last24Hours: last24HoursCount || 0,
          };
        } catch (error) {
          console.warn(`Failed to fetch stats for agent ${agent.id}:`, error);
          statsMap[agent.id] = { total: 0, last30Days: 0, last24Hours: 0 };
        }
      }

      return statsMap;
    },
    [organization?.id],
  );

  // Chat agents fetching - DEPRECATED: No longer loads chat agents
  const refreshChatAgents = useCallback(async () => {
    // Chat agents are sunset - this is now a no-op for backward compatibility
    // The state will remain with empty arrays which is the intended behavior
    return;
  }, []);

  // Voice agents fetching - Optimized for better performance using direct queries
  const refreshVoiceAgents = useCallback(async () => {
    if (
      disabled ||
      !organization?.id ||
      !state.voiceAgentsEnabled ||
      !mountedRef.current
    )
      return;

    try {
      dispatch({ type: "VOICE_AGENTS_LOADING", payload: true });

      // Use direct Supabase query instead of edge function for better performance
      const { data, error } = await supabase
        .from("voice_agents")
        .select("*")
        .eq("organization_id", organization.id)
        .eq("is_template", false)
        .neq("archived", true)
        .order("created_at", { ascending: false });

      if (!mountedRef.current) return;

      if (error) throw error;

      const agents = data || [];

      // Immediately dispatch agents without stats for instant UI update
      if (mountedRef.current) {
        dispatch({
          type: "VOICE_AGENTS_SUCCESS",
          payload: { agents },
        });
      }

      // Load stats asynchronously after agents are displayed
      if (agents.length > 0 && mountedRef.current) {
        // Indicate stats are loading
        dispatch({
          type: "VOICE_STATS_LOADING",
          payload: {
            loading: true,
            agentIds: agents.map((a) => a.id),
          },
        });

        try {
          // Get stats using optimized method
          const { stats: voiceStats, error: statsError } =
            await voiceAgentService.getVoiceAgentStatsOptimized(
              agents,
              organization.id,
            );

          if (mountedRef.current) {
            if (!statsError && voiceStats) {
              dispatch({
                type: "VOICE_STATS_SUCCESS",
                payload: { stats: voiceStats },
              });
            } else {
              console.warn("Failed to fetch voice agent stats:", statsError);
              // Initialize empty stats for each agent to prevent errors
              const emptyStats = agents.reduce(
                (acc, agent) => {
                  acc[agent.id] = {
                    total_calls: 0,
                    calls_today: 0,
                    average_duration_seconds: 0,
                    end_reasons: {},
                    total_cost: 0,
                  };
                  return acc;
                },
                {} as Record<string, VoiceAgentStats>,
              );

              dispatch({
                type: "VOICE_STATS_SUCCESS",
                payload: { stats: emptyStats },
              });
            }
          }
        } catch (statsError) {
          console.warn(
            "Exception while fetching voice agent stats:",
            statsError,
          );

          if (mountedRef.current) {
            dispatch({
              type: "VOICE_STATS_ERROR",
              payload: statsError as Error,
            });
          }
        }
      }
    } catch (error) {
      if (mountedRef.current) {
        const errorObj =
          error instanceof Error
            ? error
            : new Error("Failed to fetch voice agents");
        dispatch({ type: "VOICE_AGENTS_ERROR", payload: errorObj });
      }
    }
  }, [disabled, organization?.id, state.voiceAgentsEnabled]);

  // Check voice agents feature flag - SSR-safe
  useEffect(() => {
    if (disabled || !organization?.id) return;

    let mounted = true;

    const checkVoiceFeature = async () => {
      try {
        dispatch({
          type: "SET_VOICE_FEATURE",
          payload: { enabled: false, loading: true },
        });

        const isEnabled = await featureFlagService.isVoiceAgentsEnabled(
          organization.id,
        );

        if (mounted) {
          dispatch({
            type: "SET_VOICE_FEATURE",
            payload: { enabled: isEnabled, loading: false },
          });
        }
      } catch (error) {
        console.error("Error checking voice agents feature:", error);
        if (mounted) {
          dispatch({
            type: "SET_VOICE_FEATURE",
            payload: { enabled: false, loading: false },
          });
        }
      }
    };

    checkVoiceFeature();

    return () => {
      mounted = false;
    };
  }, [disabled, organization?.id]);

  // Organization change handler
  useEffect(() => {
    dispatch({ type: "SET_ORGANIZATION", payload: organization?.id || null });
  }, [organization?.id]);

  // Initial data loading when auth is ready - side-effect containment
  useEffect(() => {
    if (disabled || isAuthLoading || !user || !organization?.id) return;

    let mounted = true;

    const initializeData = async () => {
      try {
        // Load chat agents if data is stale
        if (isDataStale(state.chatAgentsLastFetch)) {
          await refreshChatAgents();
        }

        // Load voice agents if feature is enabled and data is stale
        if (
          state.voiceAgentsEnabled &&
          isDataStale(state.voiceAgentsLastFetch)
        ) {
          await refreshVoiceAgents();
        }

        if (mounted) {
          dispatch({ type: "SET_INITIALIZED", payload: true });
        }
      } catch (error) {
        console.error("Error initializing agent data:", error);
      }
    };

    // Small delay to avoid race conditions with auth
    timeoutRef.current = setTimeout(initializeData, 100);

    return () => {
      mounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    disabled,
    isAuthLoading,
    user,
    organization?.id,
    state.voiceAgentsEnabled,
    isDataStale,
    state.chatAgentsLastFetch,
    state.voiceAgentsLastFetch,
    refreshChatAgents,
    refreshVoiceAgents,
  ]);

  // Refresh both agent types
  const refreshAgents = useCallback(async () => {
    // Only refresh voice agents now - chat agents are deprecated
    if (state.voiceAgentsEnabled) {
      await refreshVoiceAgents();
    }
  }, [refreshVoiceAgents, state.voiceAgentsEnabled]);

  // Invalidation functions - stable references
  const invalidateAgent = useCallback(
    (agentId: string, type: "chat" | "voice") => {
      dispatch({ type: "INVALIDATE_AGENT", payload: { agentId, type } });
    },
    [],
  );

  const invalidateAllAgents = useCallback(() => {
    dispatch({ type: "INVALIDATE_ALL" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Computed properties - memoized for performance
  const computedValues = useMemo(() => {
    const allAgents = [...state.chatAgents, ...state.voiceAgents];
    const allActiveAgents = [
      ...state.activeChatAgents,
      ...state.activeVoiceAgents,
    ];

    return {
      allAgents,
      allActiveAgents,
      totalAgentCount: allAgents.length,
      isLoading:
        state.isLoadingChatAgents ||
        state.isLoadingVoiceAgents ||
        state.isLoadingVoiceFeature,
      hasError: !!(state.chatAgentsError || state.voiceAgentsError),
      isLoadingVoiceStats: state.isLoadingVoiceStats,
      voiceStatsLoadingAgents: state.voiceStatsLoadingAgents,
    };
  }, [
    state.chatAgents,
    state.voiceAgents,
    state.activeChatAgents,
    state.activeVoiceAgents,
    state.isLoadingChatAgents,
    state.isLoadingVoiceAgents,
    state.isLoadingVoiceFeature,
    state.chatAgentsError,
    state.voiceAgentsError,
    state.isLoadingVoiceStats,
    state.voiceStatsLoadingAgents,
  ]);

  // Stable context value - prevents ghost re-renders
  const contextValue = useMemo<AgentContextType>(
    () => ({
      // State
      ...state,

      // Computed
      ...computedValues,

      // Actions - stable references
      refreshAgents,
      refreshChatAgents,
      refreshVoiceAgents,
      invalidateAgent,
      invalidateAllAgents,
      reset,
    }),
    [
      state,
      computedValues,
      refreshAgents,
      refreshChatAgents,
      refreshVoiceAgents,
      invalidateAgent,
      invalidateAllAgents,
      reset,
    ],
  );

  // Show fallback during initial load if provided
  if (fallback && (isAuthLoading || !state.isInitialized)) {
    return <>{fallback}</>;
  }

  const content = (
    <AgentContext.Provider value={contextValue}>
      {children}
    </AgentContext.Provider>
  );

  // Optionally wrap with error boundary
  if (errorBoundary) {
    return <AgentErrorBoundary>{content}</AgentErrorBoundary>;
  }

  return content;
}

// Error boundary component
class AgentErrorBoundary extends React.Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      "AgentProvider Error Boundary caught an error:",
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">
            Something went wrong with agent data
          </h2>
          <p className="text-gray-600 mb-4">
            {this.state.error?.message || "An unexpected error occurred"}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: undefined })}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for consuming the context - with helpful error messages
export function useAgents(): AgentContextType {
  const context = useContext(AgentContext);

  if (context === createDefaultContextValue()) {
    // More helpful error message
    throw new Error(
      "useAgents must be used within an AgentProvider. " +
        "Make sure your component tree includes <AgentProvider> at a higher level.",
    );
  }

  return context;
}

// Selective hooks for specific data - prevents unnecessary re-renders
export function useChatAgents() {
  const {
    chatAgents,
    activeChatAgents,
    isLoadingChatAgents,
    chatAgentsError,
    refreshChatAgents,
  } = useAgents();
  return useMemo(
    () => ({
      chatAgents,
      activeChatAgents,
      isLoading: isLoadingChatAgents,
      error: chatAgentsError,
      refresh: refreshChatAgents,
    }),
    [
      chatAgents,
      activeChatAgents,
      isLoadingChatAgents,
      chatAgentsError,
      refreshChatAgents,
    ],
  );
}

export function useVoiceAgents() {
  const {
    voiceAgents,
    activeVoiceAgents,
    isLoadingVoiceAgents,
    voiceAgentsError,
    voiceAgentsEnabled,
    refreshVoiceAgents,
  } = useAgents();

  return useMemo(
    () => ({
      voiceAgents,
      activeVoiceAgents,
      isLoading: isLoadingVoiceAgents,
      error: voiceAgentsError,
      enabled: voiceAgentsEnabled,
      refresh: refreshVoiceAgents,
    }),
    [
      voiceAgents,
      activeVoiceAgents,
      isLoadingVoiceAgents,
      voiceAgentsError,
      voiceAgentsEnabled,
      refreshVoiceAgents,
    ],
  );
}

export function useAgentStats() {
  const { chatAgentStats, voiceAgentStats } = useAgents();
  return useMemo(
    () => ({
      chatStats: chatAgentStats,
      voiceStats: voiceAgentStats,
    }),
    [chatAgentStats, voiceAgentStats],
  );
}
